<script setup lang="ts">
/**
 * Grid of Lucide icons for picking an item type icon.
 * Same visual pattern as ColorSwatchPicker — a grid of buttons
 * with selection ring on the active one.
 */
import { COLOR_HEX } from "@/lib/colors"
import { lucideIcon } from "@/lib/icons"

defineProps<{
  /** Available Lucide icon names (e.g. ["box", "home", "wrench"]) */
  icons: string[]
  /** Currently selected icon name */
  modelValue: string
  /** Color to tint the selected icon (Tailwind color key) */
  color?: string
}>()

const emit = defineEmits<{
  "update:modelValue": [value: string]
}>()
</script>

<template>
  <div role="radiogroup" aria-label="Icon" class="flex flex-wrap gap-2">
    <button
      v-for="icon in icons"
      :key="icon"
      type="button"
      role="radio"
      class="flex size-10 items-center justify-center rounded-lg border transition-all"
      :class="
        modelValue === icon
          ? 'border-primary bg-primary/10 ring-primary ring-1'
          : 'border-default bg-default hover:bg-muted'
      "
      :aria-label="`${icon} icon`"
      :aria-checked="modelValue === icon"
      @click="emit('update:modelValue', icon)"
    >
      <UIcon
        :name="lucideIcon(icon)"
        class="size-5"
        :style="modelValue === icon && color ? { color: COLOR_HEX[color] ?? undefined } : undefined"
      />
    </button>
  </div>
</template>
